// React
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";

// Libraries
import PropTypes from "prop-types";
import { Container, Icon, Menu, Popup, Image } from "semantic-ui-react";
import { includes, isEmpty, map, get, some } from "lodash";

// Redux
import { connect } from "react-redux";
import { logout, changeState } from "@Actions/authentication";
import { displayNotification } from "@Actions/notifications";
import downloadFile from "@Redux/Actions/downloads";
import genericAction from "@Actions/generic";

// Components
import Text from "@Components/Text";
import ConfirmModal from "@Modals/ConfirmModal";

// Hooks
import usePanelVisibility from "@Hooks/usePanelVisibility";

// Data
import options from "./StaticData";

// Styles
import styles from "./styles.module.css";

const Header = ({
	dispatch,
	headerTitle,
	isLoggedIn,
	userName,
	userId,
	state,
	companyId,
	companyName,
	companyLogo,
	companySettings,
	chats,
	permissions,
	handleToggle,
}) => {
	const { isMobile, isMobileOrTablet } = usePanelVisibility();
	const [fileUri, setFileUri] = useState(null);
	const [userState, setUserState] = useState(state);
	const [openState, setOpenState] = useState(false);
	const [openConfirmModal, setOpenConfirmModal] = useState(false);
	const navigate = useNavigate();

	const [canLogoutWithAssignedChats, hasPendingChats] = useMemo(() => {
		const canLogout = get(companySettings, "canLogoutWithAssignedChats", true);
		const hasPending = some(chats, { userId });
		return [canLogout, hasPending];
	}, [companyId, chats, userId]);

	useEffect(() => {
		(async () => {
			if (companyLogo) {
				const file = await dispatch(downloadFile({ uri: companyLogo }));
				setFileUri(file);
			}
		})();
	}, [companyLogo]);

	useEffect(() => {
		if (userState === "No Asigna Chat" && !get(permissions, "noAssignState", false)) {
			dispatch(changeState("En Línea"));
			setUserState("En Línea");
		}
	}, [permissions, userState]);

	const getDotColor = () => {
		if (includes(["En Línea"], userState)) {
			return "greenDot";
		}
		if (includes(["No Asigna Chat"], userState)) {
			return "yellowDot";
		}
		if (includes(["Almuerzo", "Baño", "Break", "Gestión Interna", "En Reunión"], userState)) {
			return "redDot";
		}
		return "greyDot";
	};

	const onOpen = () => {
		setOpenState(true);
	};

	const onClose = () => {
		setOpenState(false);
	};

	const handleLogOut = () => {
		if (!canLogoutWithAssignedChats && hasPendingChats) {
			dispatch(displayNotification({ text: "No puede cerrar session con chats pendiente", isError: true }));
		} else {
			dispatch(logout());
		}
	};

	const handleFreeAllChats = async () => {
		setOpenConfirmModal(false);
		try {
			await dispatch(genericAction({ method: "POST", route: "chats/freeChat/all" }));
			dispatch(displayNotification({ text: "Se liberaron todos los chats.", isError: false }));
			// dispatch(logout());
		} catch (error) {
			dispatch(displayNotification({ text: "Error al liberar chats.", isError: true }));
		}
	};

	const handleChangeCompany = () => {
		if (!canLogoutWithAssignedChats && hasPendingChats) {
			dispatch(displayNotification({ text: "No puede cambiar compañia con chats pendiente", isError: true }));
		} else {
			navigate("/chooseCompany");
		}
	};

	const onChangeUserState = (value) => {
		dispatch(changeState(value));
		setUserState(value);
		onClose();
	};

	const renderOptions = () => {
		if (isEmpty(options)) return null;
		const mappedOptions = map(options, (item) => {
			if (item.value === "No Asigna Chat" && !get(permissions, "noAssignState", false)) {
				return null;
			}
			const isCurrent = userState === item.value;
			return (
				<Menu.Item
					key={item.key}
					className={isCurrent ? styles.currentMenuItem : styles.menuItem}
					onClick={
						isCurrent
							? undefined
							: () => {
									return onChangeUserState(item.value);
							  }
					}>
					<Text className={styles.text}>{item.text}</Text>
				</Menu.Item>
			);
		});

		return mappedOptions;
	};

	return (
		<div className={styles.header}>
			<Container fluid className={styles.row}>
				<div className={styles.leftContainer}>
					{isMobileOrTablet && (
						<div className={styles.regRow}>
							<Icon className={styles.icon} name="bars" link onClick={handleToggle} />
						</div>
					)}
					{companyId > 0 && (
						<>
							{!isMobileOrTablet && <Image className={styles.companyLogo} src={fileUri} />}
							{!isMobile && <Text className={styles.companyTitle}>{companyName}</Text>}
						</>
					)}
				</div>
				<div className={styles.centerContainer}>
					<Text className={styles.headerText}>{headerTitle}</Text>
				</div>
				<div className={styles.rightContainer}>
					{companyId > 0 && (
						<Popup
							className={styles.menu}
							on="click"
							position="bottom right"
							open={openState}
							onOpen={onOpen}
							onClose={onClose}
							trigger={
								<div className={styles.regRow}>
									<div className={`${styles.dot} ${styles[getDotColor()]}`} />
									<Text className={styles.userText}>{userState}</Text>
									<Icon className={styles.icon} name="caret down" />
								</div>
							}>
							<Popup.Content>
								<Menu vertical>{renderOptions()}</Menu>
							</Popup.Content>
						</Popup>
					)}
					{isLoggedIn && (
						<Popup
							className={styles.menu}
							on="click"
							position="bottom left"
							trigger={
								<div className={styles.userRow}>
									<Icon className={styles.icon} name="user" />
									<Text className={styles.userText}>{userName}</Text>
									<Icon className={styles.icon} name="caret down" />
								</div>
							}>
							<Popup.Content>
								<Menu vertical>
									<Menu.Item className={styles.menuUserItem}>
										<Icon className={styles.menuIcon} name="user" />
										<Text className={styles.text}>{userName}</Text>
									</Menu.Item>
									{get(permissions, "allowFreeAllChats", false) && (
										<Menu.Item
											className={styles.menuItem}
											onClick={() => {
												return setOpenConfirmModal(true);
											}}>
											<Icon className={styles.menuIcon} name="unlock" />
											<Text className={styles.text}>Liberar todas</Text>
										</Menu.Item>
									)}
									{companyId > 0 && (
										<Menu.Item className={styles.menuItem} onClick={handleChangeCompany}>
											<Icon className={styles.menuIcon} name="sync" />
											<Text className={styles.text}>Cambiar Compañia</Text>
										</Menu.Item>
									)}
									<Menu.Item className={styles.menuItem} onClick={handleLogOut}>
										<Icon className={styles.menuIcon} name="sign-out" />
										<Text className={styles.text}>Cerrar Sessión</Text>
									</Menu.Item>
								</Menu>
							</Popup.Content>
						</Popup>
					)}
				</div>
			</Container>
			{openConfirmModal && (
				<ConfirmModal
					visible
					icon="exclamation circle"
					header="Confirmar"
					buttonLabel="Liberar Todos"
					cancelLabel="No"
					content="Estás seguro que deseas liberar todos los chats asignados a su usuario"
					onConfirm={handleFreeAllChats}
					onDismiss={() => {
						setOpenConfirmModal(false);
					}}
				/>
			)}
		</div>
	);
};

Header.propTypes = {
	dispatch: PropTypes.func,
	headerTitle: PropTypes.string,
	isLoggedIn: PropTypes.bool,
	userName: PropTypes.string,
	userId: PropTypes.number,
	state: PropTypes.string,
	companyId: PropTypes.number,
	companyName: PropTypes.string,
	companyLogo: PropTypes.string,
	companySettings: PropTypes.object,
	chats: PropTypes.array,
	permissions: PropTypes.object,
	handleToggle: PropTypes.func,
};

function mapStateToProps({ user, company, chats }) {
	return {
		isLoggedIn: user.isLoggedIn,
		userName: user.name,
		userId: user.userId,
		state: user.state,
		companyId: company.companyId,
		companyName: company.name,
		companyLogo: company.logo,
		companySettings: company.settings,
		chats: chats.chats,
		permissions: user.permissions,
	};
}

export default connect(mapStateToProps)(Header);
